/**
 * One calculation for review, documents, and saved quotations.
 *
 * Order:
 * 1. Each line uses its override, or the catalogue amount. Complimentary lines are zero.
 * 2. A line is converted once into the quotation currency by the existing currency engine
 *    (market rate, configured markup, then configured upward rounding).
 * 3. The percentage discount is applied once to that quotation-currency subtotal.
 * 4. A rand quotation settles at that same total. No foreign markup is applied.
 * 5. A foreign quotation settles by converting the final foreign total back to rand
 *    at the recorded market rate. The marked-up rate is not used, and markup is not
 *    applied again. The final cent of an uneven deposit stays on the balance.
 *
 * settlementPolicy `market-v2` is this rule. Records without it keep `catalogue-v1`:
 * their rand total is the discounted catalogue amount and is not rewritten.
 */

export const QUOTE_CURRENCIES = ['R', 'USD', 'EUR', 'GBP', 'CAD', 'AUD'] as const;
export type QuoteCurrencyCode = (typeof QUOTE_CURRENCIES)[number];

export interface ConversionResult {
  markupAmount: number;
  exchangeRate: number;
  finalRate: number;
  markupPercentage: number;
  roundToNearest?: number;
}

export type ConvertAmount = (
  amount: number,
  fromCurrency: string,
  toCurrency: string
) => Promise<ConversionResult>;

export interface PricedLineInput {
  id: string;
  kind: 'package' | 'extra' | 'custom' | 'session';
  name: string;
  description?: string;
  /** Short "what's included" bullets, shown under the package on the client's quotation. */
  features?: string[];
  /** How long this package or session lasts, in minutes. */
  durationMinutes?: number;
  /** Links a session line back to QuoteSession.id on ad hoc quotes. */
  sessionId?: string;
  /** YYYY-MM-DD for a session line. */
  date?: string;
  /** HH:MM for a session line. */
  startTime?: string;
  pricingType: 'fixed' | 'per-hour' | 'per-each';
  units: number;
  unitAmount: number;
  sourceCurrency: string;
  overrideUnitAmount?: number | null;
  overrideCurrency?: string | null;
  complimentary?: boolean;
}

export interface QuotePricingInput {
  currency: string;
  discountPercent: number;
  depositPercent: number;
  lines: PricedLineInput[];
  convert: ConvertAmount;
}

export interface AppliedRate {
  fromCurrency: string;
  toCurrency: string;
  exchangeRate: number;
  finalRate: number;
  markupPercentage: number;
  roundToNearest?: number;
}

export interface PricedLine {
  id: string;
  kind: PricedLineInput['kind'];
  name: string;
  description?: string;
  features?: string[];
  durationMinutes?: number;
  sessionId?: string;
  date?: string;
  startTime?: string;
  pricingType: PricedLineInput['pricingType'];
  units: number;
  complimentary: boolean;
  sourceCurrency: string;
  sourceUnitMinor: number;
  sourceExtendedMinor: number;
  quoteUnitMinor: number;
  quoteExtendedMinor: number;
  zarExtendedMinor: number;
  rate?: AppliedRate;
}

export type SettlementPolicy = 'market-v2' | 'catalogue-v1';

export interface QuotePricingSnapshot {
  currency: string;
  settlementPolicy?: SettlementPolicy;
  discountPercent: number;
  depositPercent: number;
  lines: PricedLine[];
  quoteSubtotalMinor: number;
  quoteDiscountMinor: number;
  quoteTotalMinor: number;
  zarSubtotalMinor: number;
  zarDiscountMinor: number;
  zarTotalMinor: number;
  depositMinor: number;
  balanceMinor: number;
  paymentCurrency: 'R';
  vat: null;
  rates: AppliedRate[];
  settlementRate?: AppliedRate | null;
}

export function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/** All six studio currencies use two decimal places. */
export function toMinor(major: number): number {
  if (!Number.isFinite(major)) return 0;
  return Math.round(major * 100);
}

export function fromMinor(minor: number): number {
  return minor / 100;
}

/**
 * Deposit is rounded down. The final instalment is whatever remains,
 * so the two payments add up to the locked rand total.
 */
export function splitDeposit(totalMinor: number, depositPercent: number): { depositMinor: number; balanceMinor: number } {
  const safeTotal = Math.max(0, Math.round(totalMinor));
  const scaledPercent = Math.round(clampPercent(depositPercent) * 100);
  const depositMinor = Math.floor((safeTotal * scaledPercent) / 10000);
  return { depositMinor, balanceMinor: safeTotal - depositMinor };
}

function incrementDigits(digits: string): string {
  const chars = digits.split('');
  for (let index = chars.length - 1; index >= 0; index -= 1) {
    if (chars[index] !== '9') {
      chars[index] = String(Number(chars[index]) + 1);
      return chars.join('');
    }
    chars[index] = '0';
  }
  return `1${chars.join('')}`;
}

/**
 * Rand cents for a final foreign amount at the market rate.
 * exchangeRate is foreign units per 1 ZAR. USD 180.00 at 0.0607 is R 2,965.40.
 * Rounded half away from zero to the nearest cent. This must match server/quoteStore.js.
 */
export function settleForeignToZarMinor(foreignMinor: number, exchangeRate: number): number {
  const minor = Math.round(Number(foreignMinor));
  const numeric = Number(exchangeRate);
  if (!Number.isInteger(minor) || minor < 0 || !Number.isFinite(numeric) || numeric <= 0) {
    throw new Error('A market exchange rate is required.');
  }
  const text = numeric.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) throw new Error('A market exchange rate is required.');
  const fraction = match[2] || '';
  const divisor = Number(`${match[1]}${fraction}`.replace(/^0+(?=\d)/, ''));
  const numerator = `${minor}${'0'.repeat(fraction.length)}`.replace(/^0+(?=\d)/, '') || '0';
  if (!Number.isSafeInteger(divisor) || divisor <= 0) throw new Error('A market exchange rate is required.');
  let remainder = 0;
  let digits = '';
  for (let index = 0; index < numerator.length; index += 1) {
    const next = remainder * 10 + Number(numerator[index]);
    if (!Number.isSafeInteger(next)) throw new Error('The rand settlement is too large.');
    digits += String(Math.floor(next / divisor));
    remainder = next % divisor;
  }
  digits = digits.replace(/^0+(?=\d)/, '') || '0';
  if (remainder * 2 >= divisor) digits = incrementDigits(digits);
  const settled = Number(digits);
  if (!Number.isSafeInteger(settled)) throw new Error('The rand settlement is too large.');
  return settled;
}

function applyPercent(totalMinor: number, percent: number): { discountMinor: number; netMinor: number } {
  const safeTotal = Math.max(0, Math.round(totalMinor));
  const scaledPercent = Math.round(clampPercent(percent) * 100);
  const discountMinor = Math.round((safeTotal * scaledPercent) / 10000);
  return { discountMinor, netMinor: safeTotal - discountMinor };
}

export interface QuotedTotalAdjustment {
  discountPercent: number;
  depositPercent: number;
  quoteSubtotalMinor: number;
  quoteDiscountMinor: number;
  quoteTotalMinor: number;
  zarTotalMinor: number;
  depositMinor: number;
  balanceMinor: number;
}

/**
 * Discount and deposit for a subtotal that is already in the quotation currency.
 * Same rounding as priceQuote, so the figures on screen match the saved quotation.
 */
export function adjustQuotedTotals(input: {
  currency?: string;
  quoteSubtotalMinor: number;
  discountPercent: number;
  depositPercent: number;
  /** Foreign units per 1 ZAR. Used to settle a non-rand quotation. */
  settlementRate?: number | null;
}): QuotedTotalAdjustment {
  const currency = input.currency || 'R';
  const discountPercent = clampPercent(input.discountPercent);
  const depositPercent = clampPercent(input.depositPercent);
  const quoteSubtotalMinor = Math.max(0, Math.round(Number(input.quoteSubtotalMinor) || 0));
  const quoteDiscount = applyPercent(quoteSubtotalMinor, discountPercent);
  const quoteTotalMinor = quoteDiscount.netMinor;
  const rate = Number(input.settlementRate);
  const zarTotalMinor = currency === 'R'
    ? quoteTotalMinor
    : (Number.isFinite(rate) && rate > 0 ? settleForeignToZarMinor(quoteTotalMinor, rate) : quoteTotalMinor);
  const deposit = splitDeposit(zarTotalMinor, depositPercent);
  return {
    discountPercent,
    depositPercent,
    quoteSubtotalMinor,
    quoteDiscountMinor: quoteDiscount.discountMinor,
    quoteTotalMinor,
    zarTotalMinor,
    depositMinor: deposit.depositMinor,
    balanceMinor: deposit.balanceMinor,
  };
}

function unitsFor(line: PricedLineInput): number {
  const units = Number(line.units);
  if (!Number.isFinite(units) || units < 0) return 0;
  if (line.pricingType === 'fixed') return 1;
  return units;
}

function amountCurrency(line: PricedLineInput): { unit: number; currency: string } {
  if (line.overrideUnitAmount != null && line.overrideCurrency) {
    return { unit: line.overrideUnitAmount, currency: line.overrideCurrency };
  }
  return { unit: line.unitAmount, currency: line.sourceCurrency || 'R' };
}

export async function priceQuote(input: QuotePricingInput): Promise<QuotePricingSnapshot> {
  const currency = input.currency || 'R';
  const discountPercent = clampPercent(input.discountPercent);
  const depositPercent = clampPercent(input.depositPercent);
  const lines: PricedLine[] = [];
  const rates: AppliedRate[] = [];

  for (const line of input.lines) {
    const units = unitsFor(line);
    const priced = amountCurrency(line);
    const complimentary = Boolean(line.complimentary) || !(priced.unit > 0);
    const sourceUnitMinor = toMinor(priced.unit);
    const sourceExtendedMinorExact = complimentary ? 0 : Math.round(sourceUnitMinor * units);

    let quoteExtendedMinor = 0;
    let zarExtendedMinor = 0;
    let rate: AppliedRate | undefined;

    if (!complimentary && sourceExtendedMinorExact !== 0) {
      const extendedMajor = fromMinor(sourceExtendedMinorExact);
      if (priced.currency === currency) {
        quoteExtendedMinor = sourceExtendedMinorExact;
      } else {
        const converted = await input.convert(extendedMajor, priced.currency, currency);
        quoteExtendedMinor = toMinor(converted.markupAmount);
        rate = {
          fromCurrency: priced.currency,
          toCurrency: currency,
          exchangeRate: converted.exchangeRate,
          finalRate: converted.finalRate,
          markupPercentage: converted.markupPercentage,
          roundToNearest: converted.roundToNearest,
        };
        rates.push(rate);
      }

      if (currency === 'R' && priced.currency === 'R') {
        zarExtendedMinor = sourceExtendedMinorExact;
      } else if (currency === 'R') {
        const convertedZar = await input.convert(extendedMajor, priced.currency, 'R');
        zarExtendedMinor = toMinor(convertedZar.markupAmount);
        rates.push({
          fromCurrency: priced.currency,
          toCurrency: 'R',
          exchangeRate: convertedZar.exchangeRate,
          finalRate: convertedZar.finalRate,
          markupPercentage: convertedZar.markupPercentage,
          roundToNearest: convertedZar.roundToNearest,
        });
      } else if (priced.currency === 'R') {
        zarExtendedMinor = sourceExtendedMinorExact;
      }
    }

    const keepDuration = (line.kind === 'package' || line.kind === 'session') && Number(line.durationMinutes) > 0;
    lines.push({
      id: line.id,
      kind: line.kind,
      name: line.name,
      description: line.description,
      ...(line.features && line.features.length > 0 ? { features: line.features } : {}),
      ...(keepDuration ? { durationMinutes: Math.round(Number(line.durationMinutes)) } : {}),
      ...(line.kind === 'session' && line.sessionId ? { sessionId: String(line.sessionId) } : {}),
      ...(line.kind === 'session' && line.date ? { date: String(line.date) } : {}),
      ...(line.kind === 'session' && line.startTime ? { startTime: String(line.startTime) } : {}),
      pricingType: line.pricingType,
      units,
      complimentary,
      sourceCurrency: priced.currency,
      sourceUnitMinor,
      sourceExtendedMinor: sourceExtendedMinorExact,
      quoteUnitMinor: units > 0 ? Math.round(quoteExtendedMinor / units) : 0,
      quoteExtendedMinor,
      zarExtendedMinor,
      rate,
    });
  }

  const quoteSubtotalMinor = lines.reduce((sum, line) => sum + line.quoteExtendedMinor, 0);
  const quoteDiscount = applyPercent(quoteSubtotalMinor, discountPercent);
  let settlementRate = rates.find((item) => item.fromCurrency === 'R' && item.toCurrency === currency && item.exchangeRate > 0) || null;
  if (currency !== 'R' && !settlementRate) {
    const probe = await input.convert(1, 'R', currency);
    if (!(probe.exchangeRate > 0)) throw new Error('A market exchange rate is required.');
    settlementRate = {
      fromCurrency: 'R',
      toCurrency: currency,
      exchangeRate: probe.exchangeRate,
      finalRate: probe.finalRate,
      markupPercentage: probe.markupPercentage,
      roundToNearest: probe.roundToNearest,
    };
    rates.push(settlementRate);
  }

  const quoteTotalMinor = quoteDiscount.netMinor;
  const zarSubtotalMinor = currency === 'R'
    ? quoteSubtotalMinor
    : settleForeignToZarMinor(quoteSubtotalMinor, settlementRate!.exchangeRate);
  const zarTotalMinor = currency === 'R'
    ? quoteTotalMinor
    : settleForeignToZarMinor(quoteTotalMinor, settlementRate!.exchangeRate);
  const deposit = splitDeposit(zarTotalMinor, depositPercent);

  return {
    currency,
    settlementPolicy: 'market-v2',
    discountPercent,
    depositPercent,
    lines,
    quoteSubtotalMinor,
    quoteDiscountMinor: quoteDiscount.discountMinor,
    quoteTotalMinor,
    zarSubtotalMinor,
    zarDiscountMinor: zarSubtotalMinor - zarTotalMinor,
    zarTotalMinor,
    depositMinor: deposit.depositMinor,
    balanceMinor: deposit.balanceMinor,
    paymentCurrency: 'R',
    vat: null,
    rates,
    settlementRate,
  };
}

export function formatQuoteMoney(minor: number, currency: string): string {
  const sign = minor < 0 ? '-' : '';
  const absolute = Math.abs(Math.round(minor));
  const whole = Math.floor(absolute / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cents = String(absolute % 100).padStart(2, '0');
  const amount = `${sign}${whole}.${cents}`;
  if (currency === 'R') return `R ${amount}`;
  return `${currency} ${amount}`;
}
